import { useTranslation } from 'react-i18next';

import { isProblemError } from '../../api/client';
import { useGetMe } from '../../api/generated/identity/identity';
import type { Me, Recording, SignedURL } from '../../api/generated/model';
import {
  useCreateRecordingDownloadURL,
  useCreateRecordingPlaybackURL,
  useListRecordings,
} from '../../api/generated/recording/recording';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
// A paginacao por cursor ja existe na SPA, e a regra e a mesma aqui: cada pagina
// devolve `nextCursor` e nada devolve cursor de volta. Uma segunda pilha de
// cursores nesta tela seria um segundo lugar onde "voltar" pode discordar.
import { usePager } from '../enrollment/AdminOnly';
// Erro e vazio nunca juntos: a regra mora num lugar so, e nao em cada tela.
import { ListOutcome } from '../enrollment/ListOutcome';
import { formatDateInPersonTimezone } from '../../i18n/datetime';
import styles from './recordings.module.css';

/**
 * Gravacoes de aula.
 *
 * A URL assinada e pedida **na hora do clique** e usada no instante em que
 * chega — nunca guardada em estado nem em cache, pelo mesmo motivo da
 * biblioteca de materiais: URL assinada em cache e URL assinada vazando por
 * historico de navegador.
 */
export function RecordingsPage() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;
  // O acervo NAO cabe numa resposta: `listRecordings` ordena da aula mais
  // recente para a mais antiga e trunca em 50 (`recording/page.go`). Pedir sem
  // cursor e ler `items` era prometer o acervo e entregar o mes passado — o
  // professor com mais de 50 gravacoes nao tinha por onde chegar as antigas.
  const pager = usePager();
  const listQuery = useListRecordings(pager.cursor === undefined ? {} : { cursor: pager.cursor }, {
    query: { enabled: !!me },
  });

  if (!me) return null;
  const items = listQuery.data?.items ?? [];
  const nextCursor = listQuery.data?.nextCursor ?? null;

  return (
    <div className={styles.page}>
      <h1>{t('recordings.title')}</h1>
      <ListOutcome
        isPending={listQuery.isPending}
        isError={listQuery.isError}
        count={items.length}
        empty={t('recordings.empty')}
      />
      <ul className={styles.list}>
        {items.map((recording) => (
          <RecordingRow key={recording.id} recording={recording} me={me} />
        ))}
      </ul>

      {/*
        A navegacao existe mesmo na unica pagina, com os dois controles
        desabilitados: um botao que aparece so quando ha mais some justamente
        quando alguem procura por ele, e some sem dizer que nao ha mais nada.
        O rotulo vem do catalogo de cadastro porque e o mesmo par de frases
        neutras — ver o relatorio, `fronteira`.
      */}
      <nav className={styles.pager} aria-label={t('recordings.title')}>
        <Button disabled={!pager.canGoBack} onClick={pager.goBack}>
          {t('enrollment.previousPage')}
        </Button>
        <Button
          disabled={nextCursor === null}
          onClick={() => {
            if (nextCursor !== null) pager.goForward(nextCursor);
          }}
        >
          {t('enrollment.nextPage')}
        </Button>
      </nav>
    </div>
  );
}

/**
 * Traduz a recusa de emissao de URL assinada para o texto que decide o que
 * fazer em seguida.
 *
 * 409 **nao** e "erro inesperado", e nao e caso raro: e a resposta declarada
 * para gravacao sem arquivo emitivel e e hoje a unica resposta que a plataforma
 * da, porque o armazenamento de gravacao ainda nao esta provisionado
 * (api/internal/recording/handler.go). 403 e o download recusado em aula em
 * dupla, que a LGPD impede de liberar. Colapsar os tres num texto so deixava
 * quem clicou sem saber se o problema era da gravacao, da aula em dupla ou da
 * plataforma — e "tente de novo em instantes" e conselho errado nos tres.
 */
function refusalKey(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (!isProblemError(error)) return 'error.unexpected';
  if (error.status === 409) return 'recordings.notReady';
  if (error.status === 403) return 'recordings.downloadDenied';
  if (error.status === 404) return 'recordings.gone';
  return 'error.unexpected';
}

/**
 * Uma gravacao, com as suas proprias emissoes de URL.
 *
 * As duas mutacoes viviam na pagina, uma so para a lista inteira: um clique
 * acendia o giro em **todos** os botoes, e nao havia onde por o erro de uma
 * linha sem por na de todas. Estado de linha mora na linha.
 */
function RecordingRow({ recording, me }: { readonly recording: Recording; readonly me: Me }) {
  const { t } = useTranslation();
  const playback = useCreateRecordingPlaybackURL();
  const download = useCreateRecordingDownloadURL();

  // Abre na hora e descarta: a URL nunca vai para estado nem cache.
  const openSigned = (signed: SignedURL): void => {
    window.open(signed.url, '_blank', 'noopener,noreferrer');
  };

  // Reproduzir so faz sentido em `available`. Nos demais estados a emissao so
  // pode recusar, e o contrato ja manda **omitir** o controle em vez de
  // desabilita-lo (e o que a tela ja fazia com o download em aula em dupla). O
  // estado escrito logo acima diz por que nao ha botao: "em composicao" e
  // "descartada" sao a propria explicacao.
  const ready = recording.state === 'available';
  const refusal = refusalKey(playback.error) ?? refusalKey(download.error);

  return (
    // Nomear a linha e o que permite falar de *uma* gravacao: sem isso, uma
    // asercao sobre "Reproduzir" casa com o botao de qualquer linha da lista.
    <li className={styles.item} aria-label={formatDateInPersonTimezone(recording.startsAtUtc, me.timezone, me.locale)}>
      <p>{formatDateInPersonTimezone(recording.startsAtUtc, me.timezone, me.locale)}</p>
      <p>{t(`recordings.state.${recording.state}`)}</p>

      {ready ? (
        <Button
          variant="secondary"
          loading={playback.isPending}
          onClick={() => playback.mutate({ recordingId: recording.id }, { onSuccess: openSigned })}
        >
          {t('recordings.play')}
        </Button>
      ) : null}

      {ready && recording.downloadAllowed ? (
        <Button
          variant="secondary"
          loading={download.isPending}
          onClick={() => download.mutate({ recordingId: recording.id }, { onSuccess: openSigned })}
        >
          {t('recordings.download')}
        </Button>
      ) : null}

      {refusal ? <Alert tone="warning">{t(refusal)}</Alert> : null}
    </li>
  );
}
